import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Percent, History, Save, Calculator } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

/**
 * Control GLOBAL de Precio B (descuento %). Afecta a TODAS las empresas y TODOS los
 * artículos desde ahora; los documentos ya emitidos no cambian. Solo admin/master.
 *
 * El % se calcula a partir de la Tasa BCV (A) y el USDT (B) con la fórmula:
 *   % = [(A * 100) / B] - 100
 * El admin/master puede ajustar el % final antes de guardar. El historial guarda
 * BCV, USDT, % calculado y % final aplicado, con fecha/hora/autor.
 */
export const PriceBControl = () => {
  const [bcv, setBcv] = useState('');
  const [usdt, setUsdt] = useState('');
  const [applied, setApplied] = useState(0);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getPriceB();
        setApplied(res.data.discount_percent || 0);
        if (res.data.bcv != null) setBcv(res.data.bcv);
        if (res.data.usdt != null) setUsdt(res.data.usdt);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const computed = (() => {
    const a = parseFloat(bcv), b = parseFloat(usdt);
    if (!a || !b) return null;
    return Math.round(((b / a) * 100 - 100) * 100) / 100;
  })();

  const applyComputed = () => {
    if (computed != null) setApplied(computed);
  };

  const loadHistory = async () => {
    try { const h = await api.getPriceBHistory(); setHistory(h.data || []); } catch (e) { setHistory([]); }
    setShowHistory(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.setPriceB({
        discount_percent: parseFloat(applied) || 0,
        bcv: bcv === '' ? null : parseFloat(bcv),
        usdt: usdt === '' ? null : parseFloat(usdt),
        computed_percent: computed,
      });
      toast.success('SalesPRO: Precio B actualizado. Aplica a todas las empresas desde ahora; los documentos previos no cambian.');
      const res = await api.getPriceB();
      setApplied(res.data.discount_percent || 0);
      await loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: No se pudo actualizar el Precio B');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-[#E2E8F0]" data-testid="price-b-control">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Percent size={16} className="text-primary" />
          <h3 className="font-heading font-bold text-[#022C22]">Precio B — Recargo global</h3>
        </div>
        <p className="text-xs text-[#64748B] mb-3">
          El Precio A ($) es fijo. Precio B ($) = Precio A × (1 + %) y se cobra en Bs. multiplicando por la tasa BCV (Precio B × BCV). Aplica a <b>todas las empresas</b> y <b>todos los artículos</b> desde ahora; los documentos ya emitidos <b>no se modifican</b>. El % se calcula con la Tasa BCV y el USDT del día.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Tasa BCV (A)</Label>
            <Input type="number" step="0.0001" min="0" value={bcv}
              onChange={(e) => setBcv(e.target.value)} placeholder="Ej: 40.00"
              data-testid="price-b-bcv-input" />
          </div>
          <div>
            <Label className="text-xs">USDT (B)</Label>
            <Input type="number" step="0.0001" min="0" value={usdt}
              onChange={(e) => setUsdt(e.target.value)} placeholder="Ej: 50.00"
              data-testid="price-b-usdt-input" />
          </div>
          <div>
            <Label className="text-xs">% calculado</Label>
            <div className="flex items-center gap-2 h-10">
              <span className="font-heading font-bold text-lg text-primary" data-testid="price-b-computed">
                {computed != null ? `${computed}%` : '—'}
              </span>
              <Button type="button" variant="outline" size="sm" onClick={applyComputed} disabled={computed == null} data-testid="price-b-apply-computed" title="Usar el % calculado">
                <Calculator size={14} className="mr-1" /> Usar
              </Button>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-[#94A3B8] mt-1">Fórmula: % = (USDT ÷ BCV × 100) − 100</p>

        <div className="flex flex-col sm:flex-row sm:items-end gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
          <div className="flex-1">
            <Label className="text-xs">% aplicado (ajustable)</Label>
            <Input type="number" step="0.01" min="0" max="100" value={applied}
              onChange={(e) => setApplied(e.target.value)} data-testid="price-b-input" />
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-price-b-button">
            <Save size={15} className="mr-1.5" /> Guardar Precio B
          </Button>
          <Button variant="outline" onClick={loadHistory} data-testid="price-b-history-button">
            <History size={15} className="mr-1.5" /> Historial
          </Button>
        </div>

        {showHistory && (
          <div className="pt-3 mt-3 border-t border-[#E2E8F0]">
            <p className="text-xs font-semibold text-[#64748B] mb-2 flex items-center gap-1"><History size={12} /> Historial de cambios</p>
            {history.length === 0 ? (
              <p className="text-xs text-[#94A3B8]">Sin cambios registrados.</p>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-1.5">
                {history.map((h, i) => (
                  <div key={i} className="text-xs text-[#64748B] flex justify-between gap-2 border-b border-[#F1F5F9] pb-1" data-testid={`price-b-history-row-${i}`}>
                    <span>
                      <b className="text-primary">{h.new_percent}%</b> aplicado
                      {h.bcv != null && h.usdt != null && (
                        <span className="text-[#94A3B8]"> · BCV {h.bcv} / USDT {h.usdt}{h.computed_percent != null ? ` → calc ${h.computed_percent}%` : ''}</span>
                      )}
                      <span className="text-[#94A3B8]"> · {h.changed_by_name}</span>
                    </span>
                    <span className="text-[#94A3B8] shrink-0">{new Date(h.changed_at).toLocaleString('es-VE', { timeZone: 'America/Caracas' })}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PriceBControl;
